// @flow
import * as constants from './constants';

import { alerts, devices, feedback } from '~/core/constants';
import { BOOST_DURATION, BOOST_TARGET } from '~/core/constants/app-settings';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import type {
    ChangeHotWaterStatusAction,
    HotWaterStatusAction,
    SetBoostModeAction,
    StopBoostModeAction
} from './types';
import { getPreviousConfigForProduct, getStatusForProduct, getTypeForProduct } from '~/core/state/products/selectors';
import { setHotWaterStatusSuccessful, setHotWaterStatusUpdateProduct, toggleHotWaterDone } from './actions';

import { buildKey } from '~/core/utils/app-settings';
import { changedProductState } from '~/core/state/products/thermostat/actions';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import messages from './intl';
import { noop } from 'lodash/fp';
import { patchProduct } from '~/core/api/products/products.api';
import { requestAlert } from '~/core/state/alerts/actions';
import { setAppSettings } from '~/core/state/app-settings/actions';
import { undoProductChange } from '~/core/state/products/actions';

export function* setBoostModeSaga({
    payload: { productId, duration, target, controlMode }
}: SetBoostModeAction): Generator<Effect | noop, *, *> {
    const props = { id: productId };
    const productType = yield select(getTypeForProduct, props);

    const patch: {
        target?: Object
    } = {
        mode: devices.deviceModes.BOOST,
        boost: duration,
        [controlMode || 'target']: target
    };

    if (target) {
        patch.target = target;
    }

    const [, error] = yield call(handleApi(patchProduct), patch, productType, productId);

    if (error) {
        yield put(undoProductChange(constants.SET_BOOST_HEATING_HOTWATER));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
        yield put(changedProductState({ productId }));
        return;
    }

    if (target) {
        yield put(setAppSettings({ [buildKey(BOOST_TARGET, controlMode, productId)]: target }));
    }

    if (duration) {
        yield put(setAppSettings({ [buildKey(BOOST_DURATION, controlMode, productId)]: duration }));
    }

    yield put(changedProductState({ productId }));
}

function* watchSetBoostModeSaga(): Generator<Effect, *, *> {
    yield takeLatest([constants.SET_BOOST_HEATING_HOTWATER], handleSaga(setBoostModeSaga));
}

export function* stopBoostModeSaga({ payload: { productId } }: StopBoostModeAction): Generator<Effect | noop, *, *> {
    const props = { id: productId };
    const productType = yield select(getTypeForProduct, props);
    const patch = yield select(getPreviousConfigForProduct, props);
    const [, error] = yield call(handleApi(patchProduct), patch, productType, productId);

    if (error) {
        yield put(undoProductChange(constants.STOP_BOOST_HEATING_HOTWATER));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
        yield put(changedProductState({ productId }));
        return;
    }

    yield put(changedProductState({ productId }));
}

function* watchStopBoostModeSaga(): Generator<Effect, *, *> {
    yield takeLatest([constants.STOP_BOOST_HEATING_HOTWATER], handleSaga(stopBoostModeSaga));
}

export function* changeHotWaterStatusSaga({ payload }: ChangeHotWaterStatusAction): Generator<Effect | noop, *, *> {
    const { productId } = payload;
    const props = { id: productId };

    const nextValue = yield select(getStatusForProduct, props);
    const productType = yield select(getTypeForProduct, props);

    const [, error] = yield call(handleApi(patchProduct), { status: nextValue }, productType, productId);

    if (error) {
        yield put(undoProductChange(constants.TOGGLE_HOT_WATER));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }

    yield put(toggleHotWaterDone({ productId }));
}

function* watchChangeHotWaterStatusSaga(): Generator<Effect, *, *> {
    yield takeLatest(constants.TOGGLE_HOT_WATER, changeHotWaterStatusSaga);
}

export function* hotWaterStatusSaga({
    payload: { nextStartTime, nextEndTime, nextStatus, productId }
}: HotWaterStatusAction): Generator<Effect | noop, *, *> {
    const props = { id: productId };
    const productType = yield select(getTypeForProduct, props);

    yield put(setHotWaterStatusUpdateProduct({ nextStatus, productId }));

    const [, error] = yield call(handleApi(patchProduct), { status: nextStatus }, productType, productId);

    if (error) {
        yield put(undoProductChange(constants.SET_HOT_WATER_STATUS));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
        return;
    }

    yield put(
        requestAlert(messages.setHotWaterStatusSuccess, alerts.types.INFO, true, { intlValues: { nextStartTime } })
    );
    yield put(setHotWaterStatusSuccessful({ productId }));
}

function* watchHotWaterStatusSaga(): Generator<Effect, *, *> {
    yield takeLatest([constants.SET_HOT_WATER_STATUS], handleSaga(hotWaterStatusSaga));
}

export default [watchSetBoostModeSaga, watchStopBoostModeSaga, watchChangeHotWaterStatusSaga, watchHotWaterStatusSaga];



// WEBPACK FOOTER //
// ./src/core/state/products/heating-hotwater/sagas.js